<script>
import Breadcrumb from '@/components/global/breadcrumb.vue';
import InputText from 'primevue/inputtext';
import axios from "axios";
import Swal from "sweetalert2";

export default {
  name: "whatsapp-links",
  data() {
    return {
      breadcrumbs: [
        { name: 'الرئيسية', path: '/' },
        { name: 'لينكات الواتساب', path: '/whatsapp_links' },
      ],
      // ── QR / Device Status ──
      qrStatus: 'loading',   // loading | connecting | working | disconnected
      qrImage: null,
      qrNeedsScan: false,
      qrLastUpdated: null,
      qrLoading: false,
      qrPollingTimer: null,
      restarting: false,
      deletingSession: false,
      // ── WhatsApp Links ── driven entirely by academic_years (every year
      // that exists, of any stage, shows up here — see getData()).
      academicYears: [],
      loadingLinks: true,
      formSending: false,
    };
  },
  components: {
    Breadcrumb,
    InputText
  },
  computed: {
    // One card per stage, in the natural primary -> prep -> secondary
    // progression, each holding every academic year of that stage (in the
    // order the API returned them, i.e. by sort_order). A stage with no
    // years at all is simply omitted rather than shown empty.
    stageGroups() {
      const stages = [
        { key: 'primary',   label: 'المرحلة الابتدائية', subtitle: 'الصفوف من الأول إلى السادس ابتدائي' },
        { key: 'prep',      label: 'المرحلة الإعدادية',   subtitle: 'الصفوف من الأول إلى الثالث إعدادي' },
        { key: 'secondary', label: 'المرحلة الثانوية',    subtitle: 'الصفوف من الأول إلى الثالث ثانوي' },
      ];
      return stages
        .map(stage => ({ ...stage, years: this.academicYears.filter(y => y.stage === stage.key) }))
        .filter(stage => stage.years.length > 0);
    },
    qrStatusLabel() {
      const labels = {
        loading:      'جاري التحميل...',
        connecting:   'في انتظار المسح',
        working:      'تم ربط واتساب بنجاح',
        disconnected: 'غير متصل',
      };
      return labels[this.qrStatus] || this.qrStatus;
    },
    qrStatusClass() {
      return {
        'text-success fw-bold': this.qrStatus === 'working',
        'text-warning':         this.qrStatus === 'connecting',
        'text-danger':          this.qrStatus === 'disconnected',
        'text-secondary':       this.qrStatus === 'loading',
      };
    },
  },
  methods: {
    async fetchQrCode() {
      this.qrLoading = true;
      try {
        const res = await axios.get('/whatsapp/qr');
        const data = res.data?.data ?? {};
        this.qrStatus      = data.status ?? 'disconnected';
        this.qrNeedsScan   = data.needs_scan === true;
        this.qrImage       = this.qrNeedsScan ? data.qr : null;
        this.qrLastUpdated = new Date().toLocaleTimeString('ar-EG');
      } catch {
        this.qrStatus = 'disconnected';
        this.qrImage  = null;
      } finally {
        this.qrLoading = false;
      }
    },
    startQrPolling() {
      this.fetchQrCode();
      this.qrPollingTimer = setInterval(() => {
        if (this.qrStatus !== 'working') {
          this.fetchQrCode();
        } else {
          this.stopQrPolling();
        }
      }, 5000);
    },
    stopQrPolling() {
      if (this.qrPollingTimer) {
        clearInterval(this.qrPollingTimer);
        this.qrPollingTimer = null;
      }
    },
    refreshQr() {
      this.stopQrPolling();
      this.startQrPolling();
    },
    async restartSession() {
      const result = await Swal.fire({
        title: 'إعادة تشغيل الجلسة',
        text: 'هل تريد إعادة تشغيل جلسة الواتساب؟ سيتم إنشاء QR جديد إذا لم يكن متصلاً.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#25d366',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'نعم، إعادة التشغيل',
        cancelButtonText: 'إلغاء',
      });
      if (!result.isConfirmed) return;

      this.restarting = true;
      this.stopQrPolling();
      try {
        const res = await axios.post('/whatsapp/restart');
        const data = res.data?.data ?? res.data ?? {};
        if (data.status) {
          this.qrStatus    = data.status;
          this.qrNeedsScan = data.needs_scan === true;
          this.qrImage     = this.qrNeedsScan ? (data.qr ?? null) : null;
        } else {
          this.qrStatus    = 'connecting';
          this.qrNeedsScan = false;
          this.qrImage     = null;
        }
        this.qrLastUpdated = new Date().toLocaleTimeString('ar-EG');
        Swal.fire({ icon: 'success', title: 'تم', text: 'تم إعادة التشغيل بنجاح', timer: 1500, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: 'خطأ', text: e.response?.data?.message || 'فشل إعادة التشغيل' });
      } finally {
        this.restarting = false;
        this.startQrPolling();
      }
    },
    async deleteSession() {
      const result = await Swal.fire({
        title: 'حذف الجلسة',
        html: 'هل أنت متأكد من حذف جلسة الواتساب؟<br><b>سيلزم مسح QR جديد بعد الحذف.</b>',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'نعم، احذف الجلسة',
        cancelButtonText: 'إلغاء',
      });
      if (!result.isConfirmed) return;

      this.deletingSession = true;
      this.stopQrPolling();
      try {
        await axios.delete('/whatsapp/session');
        this.qrStatus    = 'disconnected';
        this.qrImage     = null;
        this.qrNeedsScan = false;
        this.qrLastUpdated = new Date().toLocaleTimeString('ar-EG');
        Swal.fire({ icon: 'success', title: 'تم الحذف', text: 'تم حذف الجلسة — يرجى مسح QR جديد', timer: 2000, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: 'خطأ', text: e.response?.data?.message || 'فشل حذف الجلسة' });
      } finally {
        this.deletingSession = false;
        this.startQrPolling();
      }
    },
    getData() {
      this.loadingLinks = true;
      axios
          .get('/whatsapp_links')
          .then((response) => {
            this.academicYears = (response.data.data || []).map(y => ({ ...y }));
          })
          .catch((error) => {
            Swal.fire({
              position: "center",
              icon: "error",
              title: error.response?.data?.message || 'تعذر تحميل الروابط',
            });
          })
          .finally(() => {
            this.loadingLinks = false;
          });
    },
    sendForm() {
      this.formSending = true;

      const links = this.academicYears.map(y => ({
        academic_year_id: y.academic_year_id,
        link_boys: y.link_boys,
        link_girls: y.link_girls,
      }));

      axios
          .post('/update_whatsapp_links', { links })
          .then(() => {
            this.formSending = false;
            Swal.fire({
              position: "center",
              icon: "success",
              title: "تم التحديث بنجاح",
            });
          })
          .catch((error) => {
            this.formSending = false;
            Swal.fire({
              position: "center",
              icon: "error",
              title: error.response?.data?.message || 'حدث خطأ',
            });
          });
    },
  },
  mounted() {
    this.startQrPolling();
    this.getData();
  },
  beforeUnmount() {
    this.stopQrPolling();
  },
}
</script>

<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="form-page">

    <!-- ══════════════════════ قسم ربط الجهاز ══════════════════════ -->
    <div class="form-card mb-4">
      <div class="form-card__header">
        <div class="form-card__header-icon"><i class="pi pi-qrcode"></i></div>
        <div>
          <span class="form-card__title">ربط جهاز واتساب</span>
          <p class="form-card__subtitle">امسح الـ QR Code لربط واتساب بالنظام</p>
        </div>
        <div class="d-flex gap-2 ms-auto flex-wrap">
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="refreshQr" :disabled="qrLoading || restarting || deletingSession">
            <i class="pi" :class="qrLoading ? 'pi-spin pi-spinner-dotted' : 'pi-refresh'"></i>
            تحديث QR
          </button>
          <button type="button" class="btn btn-outline-warning btn-sm" @click="restartSession" :disabled="qrLoading || restarting || deletingSession">
            <i class="pi" :class="restarting ? 'pi-spin pi-spinner-dotted' : 'pi-power-off'"></i>
            {{ restarting ? 'جاري...' : 'إعادة التشغيل' }}
          </button>
          <button type="button" class="btn btn-outline-danger btn-sm" @click="deleteSession" :disabled="qrLoading || restarting || deletingSession">
            <i class="pi" :class="deletingSession ? 'pi-spin pi-spinner-dotted' : 'pi-trash'"></i>
            {{ deletingSession ? 'جاري...' : 'حذف الجلسة' }}
          </button>
        </div>
      </div>
      <div class="form-card__body text-center">

        <!-- حالة working -->
        <div v-if="qrStatus === 'working'" class="py-4">
          <i class="pi pi-check-circle text-success" style="font-size: 3rem;"></i>
          <p class="mt-2 fw-bold text-success fs-5">تم ربط واتساب بنجاح ✓</p>
        </div>

        <!-- QR Code -->
        <div v-else-if="qrNeedsScan && qrImage" class="py-3">
          <img :src="'data:image/png;base64,' + qrImage" alt="QR Code" style="width: 220px; height: 220px;" />
          <p class="mt-2 text-warning fw-bold">امسح الكود بتطبيق واتساب</p>
        </div>

        <!-- connecting أو disconnected بدون QR -->
        <div v-else class="py-4">
          <i class="pi pi-wifi text-warning" style="font-size: 2.5rem;"></i>
          <p class="mt-2" :class="qrStatusClass">{{ qrStatusLabel }}</p>
        </div>

        <div class="text-muted small mt-1" v-if="qrLastUpdated">
          آخر تحديث: {{ qrLastUpdated }}
        </div>
      </div>
    </div>
    <!-- ═══════════════════════════════════════════════════════════ -->

    <div class="form-page-header">
      <div class="form-page-header__icon">
        <i class="pi pi-whatsapp"></i>
      </div>
      <div class="form-page-header__text">
        <h1 class="form-page-header__title">روابط الواتساب</h1>
        <p class="form-page-header__subtitle">روابط مجموعات الواتساب لكل صف وجنس</p>
      </div>
    </div>

    <div class="container-fluid">
      <div v-if="loadingLinks" class="text-center py-4">
        <i class="pi pi-spin pi-spinner"></i>
      </div>
      <form v-else @submit.prevent="sendForm()">
        <!-- One card per educational stage (primary/prep/secondary), each
             listing every academic year that stage actually has — driven by
             stageGroups()/academicYears, not a fixed per-grade markup block,
             so any academic year (including primary) automatically appears
             here as soon as it exists in academic_years, with an empty
             input if no link has been saved for it yet. -->
        <div class="form-card" v-for="stage in stageGroups" :key="stage.key">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-users"></i></div>
            <div>
              <span class="form-card__title">{{ stage.label }}</span>
              <p class="form-card__subtitle">{{ stage.subtitle }}</p>
            </div>
          </div>
          <div class="form-card__body">
            <div
              class="grade-group"
              v-for="(year, index) in stage.years"
              :key="year.academic_year_id"
              :style="index === stage.years.length - 1 ? 'margin-bottom: 0;' : ''"
            >
              <div class="grade-group-label">
                <i class="pi pi-tag"></i>
                {{ year.name }}
              </div>
              <div class="grade-row">
                <div class="form-field">
                  <label class="field-label">
                    <span class="gender-tag gender-tag--boys"><i class="pi pi-mars"></i> بنين</span>
                  </label>
                  <div class="wa-field">
                    <i class="wa-field__icon pi pi-whatsapp"></i>
                    <InputText v-model="year.link_boys" placeholder="https://chat.whatsapp.com/..." dir="ltr" class="w-100" />
                  </div>
                </div>
                <div class="form-field">
                  <label class="field-label">
                    <span class="gender-tag gender-tag--girls"><i class="pi pi-venus"></i> بنات</span>
                  </label>
                  <div class="wa-field">
                    <i class="wa-field__icon pi pi-whatsapp"></i>
                    <InputText v-model="year.link_girls" placeholder="https://chat.whatsapp.com/..." dir="ltr" class="w-100" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-actions">
          <button type="submit" class="btn-submit" :disabled="formSending">
            <i class="pi pi-spin pi-spinner-dotted" v-if="formSending"></i>
            <i class="pi pi-check" v-else></i>
            {{ formSending ? 'جاري الحفظ...' : 'حفظ الروابط' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>